
import { LatLng, NewsItem, Language, WeatherData, Alert } from "../types";
import { fetchDisasterNews } from "./aiService";
import { getCyberLocationName, generateNearbyCoordinates } from "./locationService";

// Clean headlines to remove "6km NNW of..." jargon
const cleanHazardHeadline = (raw: string, type: string) => {
    const parts = raw.split(" of ");
    if (parts.length > 1) {
        return `${type}: ${parts[1]}`;
    }
    return `${type} Alert: ${raw}`;
};

export const generateLocalNews = async (
    location: LatLng,
    lang: Language = 'en',
    weather?: WeatherData | null,
    hazards: Alert[] = [],
    scope: 'local' | 'city' | 'country' | 'world' = 'local'
): Promise<NewsItem[]> => {

    let locationName = '';
    try {
        locationName = await getCyberLocationName(location.lat, location.lng, lang);
    } catch (e) {
        locationName = "Regional Sector";
    }

    // 1. PRIMARY: ATTEMPT AI LIVE SEARCH (Gemini + Google Search)
    // DISABLED: To prevent quota limits (402) and ensure speed.
    // try {
    //     const aiNews = await fetchDisasterNews(location, locationName, lang, scope);
    //     // Filter out low reality scores (Rumor filtering)
    //     const verifiedNews = aiNews.filter(n => n.realityScore > 4);
    //     if (verifiedNews.length > 0) {
    //         const systemAlerts = getSimulationNews(location, locationName, lang, weather, hazards, scope)
    //             .filter(n => n.category === 'CIVIL' || n.category === 'SYSTEM');
    //         return [...verifiedNews, ...systemAlerts.slice(0, 2)];
    //     }
    // } catch (e) {
    //     console.warn("AI Live Search unavailable. Switching to Simulation Fallback.");
    // }

    // 2. FALLBACK: SIMULATION
    // Only runs if AI fails or finds 0 news.
    return getSimulationNews(location, locationName, lang, weather, hazards, scope);
};

export const getSimulationNews = (
    location: LatLng,
    locName: string,
    lang: Language,
    weather?: WeatherData | null,
    hazards: Alert[] = [],
    scope: 'local' | 'city' | 'country' | 'world' = 'local'
): NewsItem[] => {
    const isAr = lang === 'ar';
    const templates: any[] = [];
    const now = new Date();

    const sources = ["NetWatch", "GridMonitor", "MedLink", "TrafficControl", "SecurOps", "CityOS", "SkyNet Weather"];
    const getRandomSource = () => sources[Math.floor(Math.random() * sources.length)];
    const getTime = (minusMins: number) => {
        const d = new Date(now.getTime() - minusMins * 60000);
        return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };

    const filteredHazards = hazards.filter(h => {
        const dist = parseFloat(h.distance.replace('km', ''));
        if (scope === 'local') return dist < 50;
        if (scope === 'city') return dist < 200;
        return true;
    });

    if (filteredHazards.length > 0) {
        const h = filteredHazards[0];
        templates.push({
            cat: 'CIVIL',
            head: isAr ? `تنبيه: ${h.type}` : cleanHazardHeadline(h.location, h.type),
            sum: isAr ? 'وحدات الاستجابة في الموقع.' : `Emergency units dispatched. Magnitude/Severity level: ${h.severity}. Perimeter established.`,
            rScore: 9.9
        });
    }

    if (weather) {
        if (weather.condition.toLowerCase().includes('rain')) {
            templates.push({
                cat: 'WEATHER',
                head: isAr ? 'تحذير انزلاق' : 'ROAD SLICK ALERT',
                sum: isAr ? 'تحذير من انزلاق المركبات.' : 'Hydroplaning risk high on arterial roads. Reduce speed by 20%.',
                rScore: 9.5
            });
        } else if (weather.temp > 35) {
            templates.push({
                cat: 'WEATHER',
                head: isAr ? 'موجة حر' : 'HEATWAVE WARNING',
                sum: isAr ? 'درجات الحرارة مرتفعة جداً.' : 'Extreme temperatures detected. Stay hydrated.',
                rScore: 9.2
            });
        }
    }



    if (scope === 'world') {
        templates.push({
            cat: 'NEWS',
            head: isAr ? 'أخبار عالمية' : 'GLOBAL SUMMIT ALERT',
            sum: isAr ? 'مؤتمر قمة عالمي.' : 'G20 Emergency Summit called to discuss cyber-security threats.',
            rScore: 9.0
        });
        templates.push({
            cat: 'SYSTEM',
            head: isAr ? 'تحذير نظام' : 'GLOBAL NET WATCH',
            sum: isAr ? 'تحذير شبكة.' : 'Solar flare activity peaked. Satellite comms may be intermittent.',
            rScore: 8.8
        });
    }

    // Generic Fallbacks
    if (templates.length < 2) {
        templates.push({
            cat: 'TRAFFIC',
            head: isAr ? 'حالة المرور' : 'MAIN ARTERIAL FLOW',
            sum: isAr ? 'ازدحام متوسط.' : `Avg speed 45km/h. Congestion detected at Sector 4 interchange.`,
            rScore: 8.5
        });
    }

    return templates.map((t, i) => ({
        id: `sim-${scope}-${i}-${Date.now()}`,
        headline: t.head,
        source: getRandomSource(),
        time: getTime(i * 12),
        category: t.cat as any,
        location: locName,
        summary: t.sum,
        trustScore: t.rScore * 10,
        realityScore: t.rScore,
        coordinates: generateNearbyCoordinates(location, scope === 'local' ? 3 : scope === 'city' ? 10 : 50),
        url: 'https://google.com/search?q=local+news'
    }));
};
